import { MAP_TILES_X, MAP_TILES_Y } from '@sokdaq/protocol';
import type { Point, Zone } from './proximity.js';

/**
 * 사무실 맵.
 *
 * 서버와 클라이언트가 같은 정의를 본다. 클라이언트는 그리고 충돌을 판정하고,
 * 서버는 구역(zone)으로 속닥이 들리는 범위를 계산한다. 둘이 어긋나면
 * "분명 회의실 안인데 밖으로 새는" 종류의 버그가 난다.
 *
 * 배치는 손으로 그린다. 편집 UI는 이게 불편해질 때 만든다(설계 §10.1).
 */

export type TileKind = 'wall' | 'floor' | 'carpet' | 'desk' | 'plant' | 'door';

const LEGEND: Record<string, TileKind> = {
  '#': 'wall',
  '.': 'floor',
  ',': 'carpet',
  D: 'desk',
  P: 'plant',
  '+': 'door',
};

/** 밟고 지나갈 수 있는 타일. */
const WALKABLE: ReadonlySet<TileKind> = new Set<TileKind>(['floor', 'carpet', 'door']);

// 40×24. 각 줄은 정확히 MAP_TILES_X 글자여야 한다 — 아래에서 검증한다.
const LAYOUT = [
  '########################################', //  0
  '#,,,,,,,,,,#..........................P#', //  1
  '#,,,,,,,,,,#..DD...DD...DD...DD........#', //  2
  '#,,,,,,,,,,#..DD...DD...DD...DD........#', //  3
  '#,,,,,,,,,,+...........................#', //  4  회의실 문
  '#,,,,,,,,,,#...........................#', //  5
  '#,,,,,,,,,,#..DD...DD...DD...DD........#', //  6
  '#,,,,,,,,,,#..DD...DD...DD...DD........#', //  7
  '############...........................#', //  8
  '#......................................#', //  9
  '#.P...................................P#', // 10
  '#......................................#', // 11  ← 기본 스폰 지대
  '#......................................#', // 12
  '#..DD...DD.............................#', // 13
  '#..DD...DD.............................#', // 14
  '#..........................#############', // 15
  '#..........................#,,,,,,,,,,,#', // 16
  '#..DD...DD.................#,,,,,,,,,,,#', // 17
  '#..DD...DD.................#,,,,,,,,,,,#', // 18
  '#..........................+,,,,,,,,,,,#', // 19  탕비실 문
  '#..........................#,,,,,,,,,,,#', // 20
  '#.P........................#,,,,,,,,,,,#', // 21
  '#..........................#,,,,,,,,,,,#', // 22
  '########################################', // 23
] as const;

export interface MapObject {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly label: string;
  /** 상호작용했을 때 머리 위에 뜨는 문구. */
  readonly emote: string;
}

/** 다가가서 누를 수 있는 것들. 타일과 별개로 얹힌다. */
const OBJECTS: readonly MapObject[] = [
  { id: 'water-cooler', x: 13, y: 9, label: '정수기', emote: '💧 물 마시는 중' },
  { id: 'coffee', x: 30, y: 17, label: '커피머신', emote: '☕ 커피 내리는 중' },
  { id: 'printer', x: 36, y: 12, label: '복합기', emote: '🖨️ 인쇄 기다리는 중' },
  { id: 'whiteboard', x: 5, y: 1, label: '화이트보드', emote: '📝 뭔가 적는 중' },
];

/**
 * 명명된 구역.
 *
 * 벽으로 나뉜 공간이라 소리가 밖으로 새지 않는다. 좌표는 LAYOUT의
 * 카펫 영역과 정확히 일치해야 한다 — 아래에서 검증한다.
 */
const ZONES: readonly Zone[] = [
  { id: 'meeting', x: 1, y: 1, width: 10, height: 7 },
  { id: 'pantry', x: 28, y: 16, width: 11, height: 7 },
];

export interface OfficeMap {
  readonly width: number;
  readonly height: number;
  readonly tiles: readonly (readonly TileKind[])[];
  readonly objects: readonly MapObject[];
  readonly zones: readonly Zone[];
  /** 신규 입장자가 나타나는 지대의 중심. */
  readonly spawn: Point;
}

function parseLayout(): readonly (readonly TileKind[])[] {
  return LAYOUT.map((row, y) => {
    if (row.length !== MAP_TILES_X) {
      throw new Error(`맵 ${y}행의 길이가 ${row.length}입니다 (${MAP_TILES_X}이어야 함)`);
    }
    return Array.from(row, (ch, x) => {
      const kind = LEGEND[ch];
      if (kind === undefined) throw new Error(`맵 (${x}, ${y})에 알 수 없는 글자 '${ch}'`);
      return kind;
    });
  });
}

if (LAYOUT.length !== MAP_TILES_Y) {
  throw new Error(`맵 행 수가 ${LAYOUT.length}입니다 (${MAP_TILES_Y}이어야 함)`);
}

export const OFFICE_MAP: OfficeMap = {
  width: MAP_TILES_X,
  height: MAP_TILES_Y,
  tiles: parseLayout(),
  objects: OBJECTS,
  zones: ZONES,
  spawn: { x: 20, y: 11 },
};

export function tileAt(map: OfficeMap, x: number, y: number): TileKind {
  // 맵 밖은 벽으로 취급한다. 경계 검사와 충돌 검사를 한 곳에서 끝낸다.
  if (x < 0 || y < 0 || x >= map.width || y >= map.height) return 'wall';
  return map.tiles[y]![x]!;
}

/** 이 타일을 밟을 수 있는가. 오브젝트가 놓인 칸은 막힌다. */
export function isWalkable(map: OfficeMap, x: number, y: number): boolean {
  const tx = Math.floor(x);
  const ty = Math.floor(y);

  if (!WALKABLE.has(tileAt(map, tx, ty))) return false;
  return !map.objects.some((object) => object.x === tx && object.y === ty);
}

/** 좌표에서 손이 닿는 오브젝트. 인접(상하좌우 1칸)이면 닿는다. */
export function objectNear(map: OfficeMap, point: Point, reachTiles = 1.5): MapObject | null {
  let closest: MapObject | null = null;
  let closestDistance = Infinity;

  for (const object of map.objects) {
    // 오브젝트는 칸의 중심에 있다고 본다.
    const distance = Math.hypot(point.x - (object.x + 0.5), point.y - (object.y + 0.5));
    if (distance <= reachTiles && distance < closestDistance) {
      closest = object;
      closestDistance = distance;
    }
  }
  return closest;
}

export function findObject(map: OfficeMap, id: string): MapObject | null {
  return map.objects.find((object) => object.id === id) ?? null;
}

/**
 * 신규 입장자를 놓을 자리.
 *
 * 전부 같은 칸에 겹쳐 놓으면 누가 누군지 알 수 없다. userId에서 결정적으로
 * 흩뿌리되, 벽에 박히지 않도록 걸을 수 있는 칸을 찾는다.
 */
export function spawnPointFor(map: OfficeMap, userId: string): Point {
  let hash = 0x811c9dc5;
  for (let i = 0; i < userId.length; i++) {
    hash ^= userId.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  hash >>>= 0;

  // 스폰 중심 주위를 나선형으로 훑는다. 가까운 칸부터 보므로
  // 사람이 늘어나도 흩어지는 범위가 완만하게 커진다.
  const spread = 3;
  const offsets: Point[] = [];
  for (let dy = -spread; dy <= spread; dy++) {
    for (let dx = -spread; dx <= spread; dx++) {
      offsets.push({ x: dx, y: dy });
    }
  }
  offsets.sort((a, b) => Math.hypot(a.x, a.y) - Math.hypot(b.x, b.y));

  const start = hash % offsets.length;
  for (let i = 0; i < offsets.length; i++) {
    const offset = offsets[(start + i) % offsets.length]!;
    const candidate = { x: map.spawn.x + offset.x, y: map.spawn.y + offset.y };
    if (isWalkable(map, candidate.x, candidate.y)) {
      // 칸의 중심에 세운다. 모서리에 걸치면 충돌 판정이 어색해진다.
      return { x: Math.floor(candidate.x) + 0.5, y: Math.floor(candidate.y) + 0.5 };
    }
  }

  return { x: map.spawn.x + 0.5, y: map.spawn.y + 0.5 };
}
